import Point from './point';

class Line {
	slope = null;

	yIntercept = 0;

	xIntercept = 0;

	get isVertical() {
		return this.slope === undefined;
	}

	get isHorizontal() {
		return this.slope === 0;
	}

	constructor(slope, yIntercept, xIntercept) {
		this.slope = slope;
		this.yIntercept = yIntercept;
		this.xIntercept = xIntercept;
	}

	static fromCoords(x1, y1, x2, y2) {
		let slope;
		let yIntercept;
		let xIntercept;

		if (x2 === x1) {
			slope = undefined;
			yIntercept = undefined;
			xIntercept = x1;
		} else {
			slope = (y2 - y1) / (x2 - x1);
			yIntercept = y1 - slope * x1;

			if (slope === 0) {
				xIntercept = undefined;
			} else {
				xIntercept = -yIntercept / slope;
			}
		}

		return new Line(slope, yIntercept, xIntercept);
	}

	static fromPoints(point1, point2) {
		return Line.fromCoords(point1.x, point1.y, point2.x, point2.y);
	}

	isParallelWith(line) {
		return this.slope === line.slope;
	}

	isPerpendicularTo(line) {
		if (this.isVertical) {
			return line.slope === 0;
		}

		if (this.isHorizontal) {
			return line.slope === undefined;
		}

		return this.slope === -line.slope;
	}

	isCollinearWith(line) {
		return this.slope === line.slope && (this.yIntercept === line.yIntercept || this.xIntercept === line.xIntercept);
	}

	intersectsWith(line) {
		if (!this.isParallelWith(line)) {
			if (this.isVertical) {
				return line.getPointFromX(this.xIntercept);
			}
			if (line.isVertical) {
				return this.getPointFromX(line.xIntercept);
			}

			const x = (line.yIntercept - this.yIntercept) / (this.slope - line.slope);
			return this.getPointFromX(x);
		}

		return this.isCollinearWith(line);
	}

	containsPoint(point) {
		if (this.isVertical) {
			return point.x === this.xIntercept;
		}

		return point.y === this.slope * point.x + this.yIntercept;
	}

	containsSegment(lineSegment) {
		return (
			this.containsPoint(new Point(lineSegment.x1, lineSegment.y1)) &&
			this.containsPoint(new Point(lineSegment.x2, lineSegment.y2))
		);
	}

	getPerpendicularLine(point) {
		if (this.isHorizontal) {
			return new Line(undefined, undefined, point.x);
		}

		if (this.isVertical) {
			return new Line(0, point.y);
		}

		const newSlope = -1 / this.slope;
		const newYIntercept = point.y - newSlope * point.x;
		const newXIntercept = -newYIntercept / newSlope;

		return new Line(newSlope, newYIntercept, newXIntercept);
	}

	getPointFromX(x) {
		if (this.isVertical) {
			if (x === this.xIntercept) {
				return new Point(x, 0);
			}

			return null;
		}

		if (this.isHorizontal) {
			return new Point(x, this.yIntercept);
		}

		return new Point(x, this.slope * x + this.yIntercept);
	}

	getPointFromY(y) {
		if (this.isVertical) {
			return new Point(this.xIntercept, y);
		}

		if (this.isHorizontal) {
			if (y === this.yIntercept) {
				return new Point(0, y);
			}

			return null;
		}

		return new Point((y - this.yIntercept) / this.slope, y);
	}
}

export default Line;
